import type { BookerPlatformWrapperAtomPropsForTeam } from '@calcom/atoms/dist/packages/platform/atoms/booker/types';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import calApi from '../api/calApi';
import type { CommonBookerProps } from '../hooks/useCommonBookerProps';
import { Booker } from '../LazyBooker';
import { NativeBooker } from '../LazyNativeBooker';
import { logBookingError } from '../loggingUtils';
import { toNativeBookerProps } from '../nativeBookerAdapter';
import { Booking } from '../types';
import {
    isBookingValid,
    isSeatReferenceBooking,
    rescheduleClassNames,
} from '../utils';
import CloseButton from './CloseButton';
import { bookingStrings, str } from './ConfirmationModal/strings';
import { Modal } from './Modal';

const rescheduleStrings = bookingStrings.confirmation.reschedule;

interface RescheduleModalProps {
    bookerProps: BookerPlatformWrapperAtomPropsForTeam;
    isNativeBookerEnabled?: boolean;
    onClose: () => void;
}

const RescheduleModal = ({
    bookerProps,
    isNativeBookerEnabled = false,
    onClose,
}: RescheduleModalProps) => {
    const [rescheduleBookingData, setRescheduleBookingData] =
        useState<Booking | null>(null);
    const { bookingUid, customClassNames, ...rest } = bookerProps;

    useEffect(() => {
        if (bookingUid) {
            calApi
                .getBookingByUidOrSeatUid(bookingUid)
                .then((bookingData) => {
                    if (isBookingValid(bookingData.status)) {
                        setRescheduleBookingData(bookingData);
                    } else {
                        onClose();
                    }
                })
                .catch((error) => {
                    logBookingError(
                        'Failed to get booking data (reschedule flow)',
                        {
                            bookingUid,
                            additionalInfoToLog: {
                                eventSlug: bookerProps.eventSlug,
                                teamId: bookerProps.teamId,
                                isTeamEvent: bookerProps.isTeamEvent,
                                bookingUid,
                            },
                            errorMessage: error.message,
                            ...error,
                        }
                    );
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const metadata = useMemo(() => {
        if (!rescheduleBookingData) {
            return {};
        }

        // Case "Share attendee information between guests" option is enabled
        if (isSeatReferenceBooking(rescheduleBookingData)) {
            const attendee = rescheduleBookingData.attendees.find(
                (attendee) => attendee.seatUid === bookingUid
            );
            return attendee?.metadata;
        }

        return rescheduleBookingData.metadata;
    }, [rescheduleBookingData, bookingUid]);

    if (!rescheduleBookingData) {
        return null;
    }

    return (
        <Modal
            width='65vw'
            header={
                <Header>
                    <span>{str(rescheduleStrings.title)}</span>
                    <CloseButton onClick={onClose} />
                </Header>
            }
            dataAuto='ssr-cal-booking-reschedule-modal'
        >
            <Wrapper>
                {isNativeBookerEnabled ? (
                    <NativeBooker
                        {...toNativeBookerProps(
                            rest as unknown as CommonBookerProps
                        )}
                        rescheduleUid={bookingUid}
                        metadata={metadata as Record<string, string>}
                    />
                ) : (
                    <Booker
                        {...rest}
                        customClassNames={customClassNames}
                        rescheduleUid={bookingUid}
                        metadata={metadata}
                    />
                )}
            </Wrapper>
        </Modal>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    .${rescheduleClassNames.atomsWrapper} .main {
        margin: 0;
    }
`;

const Header = styled.div`
    text-align: center;
    display: flex;
    flex-direction: column;
    height: 58px;
    background-color: #f5f5f7;
    line-height: 58px;
    position: relative;
    padding: 0 32px;
    font-weight: 550;
    font-size: 16px;
    color: ${({ theme }) => theme.bookerPrimaryColor};

    span {
        width: fit-content;
    }
`;
export default RescheduleModal;
